import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Rarity } from '#src/__generated__/globalTypes.ts';
import { DeliverableReward } from '#src/components/3d/DeliverableReward/index.tsx';
import { InGameCurrency3d } from '#src/components/3d/InGameCurrency/index.tsx';
import { getCardRepresentation } from '#src/lib/cards.ts';
import { isType } from '#src/lib/gql/index.ts';
import { lazy } from '#src/lib/retry.ts';

import type { Reward_AnyRewardInterface } from './__generated__/index.graphql.ts';

const Card = lazy(
  async () => import('@sorare/trois/src/atoms/Card/Card'),
  'Card'
);

const Essence = lazy(
  async () => import('@sorare/trois/src/atoms/Essence'),
  'Essence'
);

const ConversionCredit = lazy(
  async () => import('@sorare/trois/src/atoms/ConversionCredit'),
  'ConversionCredit'
);

const Picture = lazy(
  async () => import('@sorare/trois/src/atoms/Picture'),
  'Picture'
);

type Props = {
  reward: Reward_AnyRewardInterface;
};

export const Reward = ({ reward }: Props) => {
  if (isType(reward, 'AnyCardReward')) {
    return <Card card={getCardRepresentation(reward.card)} />;
  }
  if (isType(reward, 'InGameCurrencyReward')) {
    return <InGameCurrency3d item={reward.config} />;
  }

  if (isType(reward, 'CardShardsReward')) {
    return (
      <Essence
        rarity={
          (
            {
              [Rarity.common]: 'common',
              [Rarity.custom_series]: 'limited',
              [Rarity.limited]: 'limited',
              [Rarity.rare]: 'rare',
              [Rarity.super_rare]: 'super_rare',
              [Rarity.unique]: 'unique',
            } as const
          )[reward.rarity]
        }
        scale={0.5}
      />
    );
  }

  if (isType(reward, 'ShopItemReward')) {
    return <Picture url={reward.shopItem.pictureUrl} />;
  }

  if (isType(reward, 'AnyConversionCreditReward')) {
    return <ConversionCredit scale={0.25} />;
  }

  if (isType(reward, 'DeliverableItemReward')) {
    return <DeliverableReward deliverableItem={reward.deliverableItem} />;
  }

  // eslint-disable-next-line no-console
  console.error('Unknown reward type', reward);
  return null;
};

Reward.fragments = {
  AnyRewardInterface: gql`
    fragment Reward_AnyRewardInterface on AnyRewardInterface {
      id
      ... on AnyCardReward {
        id
        card {
          slug
          ...getCardRepresentation_anyCardInterface
        }
      }
      ... on InGameCurrencyReward {
        id
        config {
          id
          ...InGameCurrency3d_inGameCurrencyReward
        }
      }
      ... on CardShardsReward {
        id
        rarity
      }
      ... on ShopItemReward {
        id
        shopItem {
          id
          pictureUrl
        }
      }
      ... on DeliverableItemReward {
        id
        deliverableItem {
          slug
          ...DeliverableReward_deliverableItemInterface
        }
      }
      ... on AnyConversionCreditReward {
        id
      }
    }
    ${getCardRepresentation.fragments.anyCardInterface}
    ${DeliverableReward.fragments.deliverableItemInterface}
    ${InGameCurrency3d.fragments.InGameCurrencyRewardConfig}
  ` as TypedDocumentNode<Reward_AnyRewardInterface>,
};
